import { BaseStore, getOrCreateStore } from '@Di'
import { compact, result } from '@Helpers/functional'
import { action, observable } from 'mobx'
import { compose, filter } from 'ramda'
import {
  isFeatured,
  isPinned,
  withActivities,
  withAds,
  withFeaturedPosts,
  withPinnedPosts,
} from './helpers'

const mergeNonNulls = (obj, props) => {
  Object.keys(props).forEach((key) => {
    if (props[key] !== null) {
      obj[key] = props[key]
    }
  })

  return obj
}

export class HubPostsStore extends BaseStore {
  @observable posts = []
  @observable filtersByHubId = observable.map()
  @observable byHubId = observable.map()

  getById(id) {
    return this.posts.find((item) => String(item.id) === String(id))
  }

  @action
  setFilters(hubId, filters) {
    this.filtersByHubId.set(String(hubId), filters)
  }

  filterByIds(ids) {
    return compact(ids.map((postId) => this.posts.find((p) => p.id === postId)))
  }

  @action
  postStoreLoad(data) {
    data.forEach((post) => {
      this.add(post)
    })
  }

  @action
  add(data) {
    let post = this.getById(data.id)

    if (post) {
      mergeNonNulls(post, data)
    } else {
      post = this.createPost(data)
      this.posts.push(post)
    }
  }

  createPost(data) {
    return observable({
      id: null,
      status: 'draft',
      type: 'post',
      ...data,
      children: (data && data.children) || [],
      title_image: (data && data.title_image) || {},
      collection_id: (data && data.collection_id) || null,
    })
  }

  @action
  hubPostStoreLoad(hubId, postIds, filters, append = false) {
    const newPostIds = postIds.reduce(
      (arr, postId) => {
        arr.push(postId)
        return arr
      },
      append ? this.byHubId.get(String(hubId)) || [] : []
    )

    this.byHubId.set(String(hubId), newPostIds)
    this.setFilters(hubId, filters)
  }

  fetchPosts = async (hubId, filters) => {
    const resp = await this.request({
      path: `/hubs/${hubId}/posts`,
      query: {
        limit: 16,
        ...filters,
      },
    })

    this.postStoreLoad(resp.data)

    this.hubPostStoreLoad(
      hubId,
      resp.data.map((x) => String(x.id)),
      filters
    )

    return this.posts
  }

  getPosts(hubId) {
    const existing = this.byHubId.get(String(hubId))
    const filters = this.filtersByHubId.get(String(hubId))

    const getFilterValue = (key) => {
      return result(key, filters) || null
    }

    if (existing) {
      return existing.reduce((xs, x) => {
        const p = this.getById(x)
        let passed = true

        if (!p || p.status !== 'published') {
          passed = false
        }

        const usernameFilter = getFilterValue('username')
        const postUsername = result('user.username', p)
        if (usernameFilter && usernameFilter !== postUsername) {
          passed = false
        }

        const collectionFilter = getFilterValue('collection')
        const postCollection = result('collection.slug', p)
        if (collectionFilter && collectionFilter !== postCollection) {
          passed = false
        }

        const tagFilter = getFilterValue('tag')
        if (tagFilter && p.tags && p.tags.indexOf(tagFilter) === -1) {
          passed = false
        }

        if (passed) {
          xs.push(p)
        }
        return xs
      }, [])
    } else {
      return []
    }
  }

  getActivities(hubId) {
    // ToDo: Implement SparqMemberActivitiesStore.getActivities
    // return this.SparqMemberActivitiesStore.getActivities(hubId)
    return []
  }

  getFeatured(hubId) {
    // ToDo: Implement WidgetsStore.getFeaturedByCollection
    // return this.WidgetsStore.getFeaturedByCollection(this.getCollection(hubId))
    return []
  }

  getPinned(hubId) {
    // ToDo: Implement WidgetsStore.getFeaturedByCollection
    // return this.WidgetsStore.getPinnedByCollection(this.getCollection(hubId))
    return []
  }

  getUnPinned(hubId) {
    // ToDo: Implement WidgetsStore.getUnPinnedByCollection
    // return this.WidgetsStore.getUnPinnedByCollection(this.getCollection(hubId))
    return []
  }

  getInlineAds(hubId) {
    // ToDo: Implement WidgetsStore.getAdsByContext
    // return this.WidgetsStore.getAdsByContext('inline', 'stream', this.getCollection(hubId))
    return []
  }

  getFullAds(hubId) {
    // ToDo: Implement WidgetsStore.getAdsByContext
    // return this.WidgetsStore.getAdsByContext('full', 'stream', this.getCollection(hubId))
    return []
  }

  getFilters(hubId) {
    return this.filtersByHubId.get(String(hubId))
  }

  getItems(hubId) {
    const posts = this.getPosts(hubId)
    const featured = this.getFeatured(hubId)
    const pinned = this.getPinned(hubId)
    const werePinned = this.getUnPinned(hubId)
    const fullAds = this.getFullAds(hubId)
    const inlineAds = this.getInlineAds(hubId)
    const activities = this.getActivities(hubId)

    // ToDo: Implement DeviceStore
    // const { screenSize } = this.DeviceStore
    const screenSize = 'large'
    const wasPinned = isPinned
    const unpinnedAndUnfeaturedPosts = filter(
      (post) =>
        !isFeatured(featured, post) && !isPinned(pinned, post) && !wasPinned(werePinned, post),
      posts
    )

    const xform = compose(
      withAds(featured, fullAds, inlineAds),
      withFeaturedPosts(featured, screenSize),
      withActivities(activities),
      withPinnedPosts([
        ...werePinned,
        ...filter(
          (widget) =>
            werePinned.every(
              (x) => x.post_ids && widget.post_ids && x.post_ids[0] !== widget.post_ids[0]
            ),
          pinned
        ),
      ])
    )
    return xform(unpinnedAndUnfeaturedPosts)
  }

  @action
  togglePostLike = (post) => {
    this.togglePostLikeAsync(post)
    this.add(post)
  }

  togglePostLikeAsync = async (post) => {
    try {
      if (post.user_context.liked) {
        post.user_context.liked = false
        post.like_count--
        await this.unlike(post)
      } else {
        post.user_context.liked = true
        post.like_count++
        await this.like(post)
      }
    } catch (e) {
      if (e.error) {
        this.error = e.error
      }
      return
    }
  }

  like = async (post) => {
    await this.request({
      path: `/hubs/${post.hub_id}/posts/published/${post.id}/likes`,
      method: 'POST',
    })
  }

  unlike = async (post) => {
    await this.request({
      path: `/hubs/${post.hub_id}/posts/published/${post.id}/likes`,
      method: 'DELETE',
    })
  }
}
export default getOrCreateStore('HubPostsStore', HubPostsStore)
